"use client";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { usePowerSearch } from "../contexts/power-search-context";
import SingleSelect from "../SingleSelect";
import { useState } from "react";
import { useSelector } from "react-redux";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import { POWER_SEARCH_EVENT_NAMES } from "shared/trackEvents/constants";

const RangeFilter = ({
  title,
  minOptsList,
  maxOptsList,
  minKey,
  maxKey,
  currentMinVal,
  currentMaxVal,
  updateFilter,
  validateRange,
}) => {
  const currentUser = useSelector((state) => state.users.user);

  // Helper function to track filter events
  const trackFilterEvent = (value) => {
    if (!value) return;

    let eventName;
    if (title === "Market Capital") {
      eventName = isOpenRemunerationPlan(currentUser)
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_MARKET_CAP_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_MARKET_CAP_CLICKED;
    } else if (title === "Age") {
      eventName = isOpenRemunerationPlan(currentUser)
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_AGE_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_AGE_CLICKED;
    } else if (title === "Time in Current Role") {
      eventName = isOpenRemunerationPlan(currentUser)
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_TIME_IN_CURRENT_ROLE_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_TIME_IN_CURRENT_ROLE_CLICKED;
    } else if (title === "Base Remuneration") {
      eventName = isOpenRemunerationPlan(currentUser)
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_BASE_REMUNERATION_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_BASE_REMUNERATION_CLICKED;
    } else if (title === "Total Remuneration") {
      eventName = isOpenRemunerationPlan(currentUser)
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_TOTAL_REMUNERATION_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_TOTAL_REMUNERATION_CLICKED;
    }

    if (eventName) {
      mixpanelTrack(eventName, {
        userEmail: currentUser?.email,
      });
    }
  };
  const getFilteredMaxOptions = () => {
    if (!currentMinVal) return maxOptsList;
    const minValue = Number.parseFloat(currentMinVal.value || currentMinVal);
    return maxOptsList.filter(
      (opt) => Number.parseFloat(opt.value) >= minValue
    );
  };

  // Filter min options based on max selection
  const getFilteredMinOptions = () => {
    if (!currentMaxVal) return minOptsList;
    const maxValue = Number.parseFloat(currentMaxVal.value || currentMaxVal);
    return minOptsList.filter(
      (opt) => Number.parseFloat(opt.value) <= maxValue
    );
  };

  const hasValues = currentMinVal || currentMaxVal;
  const isValidRange =
    !currentMinVal ||
    !currentMaxVal ||
    Number.parseFloat(currentMinVal.value || currentMinVal) <=
      Number.parseFloat(currentMaxVal.value || currentMaxVal);

  return (
    <div>
      <Form.Label
        style={{ fontSize: "14px", fontWeight: "500", color: "#AF6A3A" }}
      >
        {title}
      </Form.Label>
      <div className="range-filter-container">
        <Row className="g-2 mb-2">
          <Col xs={6}>
            <SingleSelect
              value={currentMinVal}
              onChange={(value) => {
                updateFilter(minKey, value);
                // Validate after setting
                setTimeout(
                  () => validateRange(value, currentMaxVal, minKey, maxKey),
                  100
                );
                trackFilterEvent(value);
              }}
              placeholder="Min"
              options={getFilteredMinOptions().map((o) => ({
                label: o.title,
                value: o.value,
              }))}
              isClearable={true}
            />
          </Col>
          <Col xs={6}>
            <SingleSelect
              value={currentMaxVal}
              onChange={(value) => {
                updateFilter(maxKey, value);
                // Validate after setting
                setTimeout(
                  () => validateRange(currentMinVal, value, minKey, maxKey),
                  100
                );
                trackFilterEvent(value);
              }}
              placeholder="Max"
              options={getFilteredMaxOptions().map((o) => ({
                label: o.title,
                value: o.value,
              }))}
              isClearable={true}
            />
          </Col>
        </Row>

        {/* Range display and validation */}
        {hasValues && (
          <div
            className={`range-display ${!isValidRange ? "invalid" : "valid"}`}
          >
            <div className="d-flex justify-content-between align-items-center">
              <small className="range-text">
                {currentMinVal && currentMaxVal
                  ? `${currentMinVal.label} - ${currentMaxVal.label}`
                  : currentMinVal
                  ? `${currentMinVal.label}+`
                  : `Up to ${currentMaxVal.label}`}
              </small>
              <Button
                variant="link"
                size="sm"
                className="p-0 clear-range-btn"
                onClick={() => {
                  updateFilter(minKey, null);
                  updateFilter(maxKey, null);
                }}
              >
                <i className="fa fa-times" />
              </Button>
            </div>
            {!isValidRange && (
              <small className="text-danger">
                <i className="fa fa-exclamation-triangle me-1" />
                Range corrected automatically
              </small>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const MoreFiltersSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    isMoreFiltersExpanded,
    toggleMoreFilters,
    filterOptions,
    // Min/Max filter states:
    minTime,
    maxTime,
    minAge,
    maxAge,
    minRemuneration,
    maxRemuneration,
    minRevenue,
    maxRevenue,
    minBaseRem,
    maxBaseRem,
    minMarketCap,
    maxMarketCap,
    updateFilter,
  } = usePowerSearch();

  const ctopOptions = filterOptions.company_top_filter_options || {};
  const {
    min_market_cap = [],
    max_market_cap = [],
    min_age = [],
    max_age = [],
    min_time = [],
    max_time = [],
    min_base_rem = [],
    max_base_rem = [],
    min_rem = [],
    max_rem = [],
    revenue = { min_revenue: [], max_revenue: [] },
  } = ctopOptions;

  // Validation helper
  const validateRange = (minVal, maxVal, minKey, maxKey) => {
    if (!minVal || !maxVal) return true; // Allow empty values

    const minNum = Number.parseFloat(minVal.value || minVal);
    const maxNum = Number.parseFloat(maxVal.value || maxVal);

    if (minNum > maxNum) {
      // Auto-correct: if min > max, swap them
      updateFilter(minKey, maxVal);
      updateFilter(maxKey, minVal);
      return false;
    }
    return true;
  };

  return (
    <div>
      <div
        className="d-flex align-items-center mb-2"
        style={{ cursor: "pointer" }}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <Button
          variant="link"
          size="sm"
          style={{
            display: "inline-block",
            padding: 0,
            margin: 0,
            textDecoration: "none",
            marginRight: "4px",
            focus: "none",
            outline: "none",
            border: "none",
            boxShadow: "none",
            fontWeight: "bold",
          }}
        >
          {isExpanded ? "−" : "+"}
        </Button>
        <p
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            margin: 0,
          }}
        >
          MORE FILTERS
        </p>
      </div>
      <div className={`${isExpanded ? "d-block" : "d-none"}`}>
        <RangeFilter
          title="Market Capital"
          minOptsList={min_market_cap}
          maxOptsList={max_market_cap}
          minKey="minMarketCap"
          maxKey="maxMarketCap"
          currentMinVal={minMarketCap}
          currentMaxVal={maxMarketCap}
          updateFilter={updateFilter}
          validateRange={validateRange}
        />
        <div style={{ height: "16px" }} />
        <RangeFilter
          title="Revenue"
          minOptsList={revenue.min_revenue}
          maxOptsList={revenue.max_revenue}
          minKey="minRevenue"
          maxKey="maxRevenue"
          currentMinVal={minRevenue}
          currentMaxVal={maxRevenue}
          updateFilter={updateFilter}
          validateRange={validateRange}
        />
        <div style={{ height: "16px" }} />
        <RangeFilter
          title="Age"
          minOptsList={min_age}
          maxOptsList={max_age}
          minKey="minAge"
          maxKey="maxAge"
          currentMinVal={minAge}
          currentMaxVal={maxAge}
          updateFilter={updateFilter}
          validateRange={validateRange}
        />
        <div style={{ height: "16px" }} />
        <RangeFilter
          title="Time in Current Role"
          minOptsList={min_time}
          maxOptsList={max_time}
          minKey="minTime"
          maxKey="maxTime"
          currentMinVal={minTime}
          currentMaxVal={maxTime}
          updateFilter={updateFilter}
          validateRange={validateRange}
        />
        <div style={{ height: "16px" }} />
        <RangeFilter
          title="Base Remuneration"
          minOptsList={min_base_rem}
          maxOptsList={max_base_rem}
          minKey="minBaseRem"
          maxKey="maxBaseRem"
          currentMinVal={minBaseRem}
          currentMaxVal={maxBaseRem}
          updateFilter={updateFilter}
          validateRange={validateRange}
        />
        <div style={{ height: "16px" }} />
        <RangeFilter
          title="Total Remuneration"
          minOptsList={min_rem}
          maxOptsList={max_rem}
          minKey="minRemuneration"
          maxKey="maxRemuneration"
          currentMinVal={minRemuneration}
          currentMaxVal={maxRemuneration}
          updateFilter={updateFilter}
          validateRange={validateRange}
        />
      </div>

      <style jsx>{`
        .more-filters-container {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
        }

        .more-filter-header {
          padding: 12px 16px;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          transition: background-color 0.2s ease;
        }

        .more-filter-header:hover {
          background: #e9ecef;
        }

        .more-filters-content {
          padding: 16px;
        }

        .filter-group {
          margin-bottom: 20px;
        }

        .filter-group:last-child {
          margin-bottom: 0;
        }

        . {
          font-weight: 600;
          font-size: 14px;
          color: #495057;
          margin-bottom: 8px;
        }

        .range-filter-container {
          position: relative;
        }

        .range-display {
          margin-top: 4px;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .range-display.valid {
          background: #d1ecf1;
          border: 1px solid #bee5eb;
          color: #0c5460;
        }

        .range-display.invalid {
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }

        .range-text {
          font-weight: 500;
        }

        .clear-range-btn {
          color: #6c757d;
          font-size: 12px;
        }

        .clear-range-btn:hover {
          color: #dc3545;
        }
      `}</style>
    </div>
  );
};

export default MoreFiltersSection;
